import React, { useState, useEffect } from 'react';
import { Wallet, Coins, CheckCircle, AlertCircle, Copy, ExternalLink, Clock } from 'lucide-react';
import axios from 'axios';
import { ethers } from 'ethers';

function App() {
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking');

  useEffect(() => {
    checkServerStatus();
  }, []);

  useEffect(() => {
    validateAddress();
  }, [address]);

  const checkServerStatus = async () => {
    try {
      await axios.get('/api/health');
      setServerStatus('online');
    } catch (error) {
      setServerStatus('offline');
    }
  };

  const validateAddress = () => {
    if (address.length === 0) {
      setIsValidAddress(false);
      return;
    }
    
    const isValid = ethers.isAddress(address);
    setIsValidAddress(isValid);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidAddress) {
      setMessage('Please enter a valid Ethereum address');
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await axios.post('/api/faucet', { address });
      
      setMessage(`Success! ${response.data.amount} sent to ${address.slice(0, 6)}...${address.slice(-4)}`);
      setMessageType('success');
      setAddress('');
      
      // Show transaction details
      setTimeout(() => {
        setMessage(`Transaction Hash: ${response.data.transactionHash}`);
      }, 3000);
      
    } catch (error) {
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else if (error.response?.status === 429) {
        setMessage('Rate limit exceeded. You can only request once per hour.');
      } else {
        setMessage('An error occurred. Please try again later.');
      }
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Address copied to clipboard!');
    setMessageType('info');
    setTimeout(() => setMessage(''), 2000);
  };

  const getStatusColor = () => {
    switch (serverStatus) {
      case 'online': return 'text-green-500';
      case 'offline': return 'text-red-500';
      default: return 'text-yellow-500';
    }
  };

  const getStatusText = () => {
    switch (serverStatus) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      default: return 'Checking...';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
              <Coins className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            MHA Testnet Faucet
          </h1>
          <p className="text-xl text-white/90 mb-6">
            Get free testnet MHA tokens for development and testing
          </p>
          
          {/* Server Status */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
            <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
            <span className="text-white text-sm">
              Server: {getStatusText()}
            </span>
          </div>
        </div>

        {/* Main Card */}
        <div className="max-w-2xl mx-auto">
          <div className="glass-effect rounded-2xl p-8 shadow-2xl">
            {/* Network Info */}
            <div className="mb-8 p-4 bg-blue-500/20 rounded-xl">
              <div className="flex items-center justify-between text-white">
                <div>
                  <h3 className="font-semibold">Network</h3>
                  <p className="text-sm opacity-90">MHA Testnet</p>
                </div>
                <div className="text-right">
                  <h3 className="font-semibold">Amount</h3>
                  <p className="text-sm opacity-90">0.1 MHA</p>
                </div>
                <div className="text-right">
                  <h3 className="font-semibold">Rate Limit</h3>
                  <p className="text-sm opacity-90">1 per hour</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="address" className="block text-white font-medium mb-2">
                  Your Wallet Address
                </label>
                <div className="relative">
                  <Wallet className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="0x..."
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                      address.length > 0
                        ? isValidAddress
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : 'border-gray-300 bg-white'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    disabled={isLoading}
                  />
                  {address.length > 0 && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(address)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {address.length > 0 && !isValidAddress && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Please enter a valid Ethereum address
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isValidAddress || isLoading || serverStatus !== 'online'}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                  isValidAddress && !isLoading && serverStatus === 'online'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 transform hover:scale-105'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Coins className="w-5 h-5" />
                    Request 0.1 MHA
                  </>
                )}
              </button>
            </form>

            {/* Message Display */}
            {message && (
              <div className={`mt-6 p-4 rounded-xl flex items-center gap-3 ${
                messageType === 'success' 
                  ? 'bg-green-100 border border-green-300 text-green-800'
                  : messageType === 'error'
                  ? 'bg-red-100 border border-red-300 text-red-800'
                  : 'bg-blue-100 border border-blue-300 text-blue-800'
              }`}>
                {messageType === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : messageType === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                ) : (
                  <Clock className="w-5 h-5 text-blue-600" />
                )}
                <span className="text-sm font-medium">{message}</span>
              </div>
            )}

            {/* Rate Limit Info */}
            <div className="mt-8 p-4 bg-yellow-500/20 rounded-xl">
              <div className="flex items-center gap-2 text-yellow-800">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Rate Limit: One request per hour per IP address
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-white/70">
          <p className="text-sm">
            This faucet is for testing purposes only. Tokens have no real value.
          </p>
          <div className="mt-4 flex justify-center gap-4 text-xs">
            <a 
              href="https://rpc.testnet.magicalhash.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-white transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              RPC Endpoint
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
